import * as WebBrowser from 'expo-web-browser';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import validator from 'validator';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_PADDING } from '@/components/base-action-sheet';
import { EmailInput } from '@/components/email-input';
import { FormattedMarkdown } from '@/components/formatted-markdown';
import { Image, ScrollView, Text, TextStyle, View } from 'react-native';
import { SCREEN_PADDING } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const BANNER_HEIGHT = 120;
const BANNER_HEIGHT_SMALL = 88;

interface Props {
  bannerImageUrl?: string | null;
  bannerText?: string | null;
  isSubmitting?: boolean;
  onAccept: (email?: string) => void;
  onDecline: () => void;
  termsAcceptanceDisclaimer?: string | null;
  termsSummary?: string | null;
  termsUrl?: string | null;
  userEmail?: string | null;
}

export const LoyaltyTermsContent: React.FC<Props> = ({
  bannerImageUrl,
  bannerText,
  isSubmitting = false,
  onAccept,
  onDecline,
  termsAcceptanceDisclaimer,
  termsSummary,
  termsUrl,
  userEmail,
}) => {
  const { bottom } = useSafeAreaInsets();
  const { isSmallLayout } = useLayoutContext();
  const [styles] = useStyles();

  const [email, setEmail] = useState('');
  const [isEmailFocused, setIsEmailFocused] = useState(false);

  const isEmailValid = validator.isEmail((userEmail ?? email).trim());

  const markdownTextStyle = isSmallLayout
    ? styles.markdownSmall
    : styles.markdown;

  const bannerTextBlock = bannerText ? (
    <FormattedMarkdown markdown={bannerText} textStyle={markdownTextStyle} />
  ) : null;

  // Replaces the generic terms link rather than stacking with it, since a
  // disclaimer is expected to link its own terms
  const termsAcceptanceDisclaimerBlock = termsAcceptanceDisclaimer ? (
    <FormattedMarkdown
      markdown={termsAcceptanceDisclaimer}
      textStyle={markdownTextStyle}
    />
  ) : null;

  const bannerBlock = bannerImageUrl ? (
    <Image
      accessibilityIgnoresInvertColors
      accessible={false}
      resizeMode="contain"
      source={{ uri: bannerImageUrl }}
      style={[
        styles.banner,
        { height: isSmallLayout ? BANNER_HEIGHT_SMALL : BANNER_HEIGHT },
      ]}
      testID="loyalty-terms-modal.banner"
    />
  ) : null;

  const termsLinkBlock = termsUrl ? (
    <Text
      onPress={() => WebBrowser.openBrowserAsync(termsUrl)}
      style={styles.termsLink}
      testID="loyalty-terms-modal.terms-link"
    >
      {i18n.t('loyalty.read_full_terms')}
    </Text>
  ) : null;

  const emailInputBlock = !userEmail ? (
    <EmailInput
      containerStyle={styles.emailInput}
      error={
        email && !isEmailValid && !isEmailFocused
          ? i18n.t('toast.invalid_email')
          : null
      }
      onBlur={() => setIsEmailFocused(false)}
      onChangeText={setEmail}
      onFocus={() => setIsEmailFocused(true)}
      testID="loyalty-terms-modal.email"
      value={email}
    />
  ) : null;

  return (
    <ScrollView
      contentContainerStyle={[
        styles.scrollContent,
        // The sheet zeroes its own bottom padding so the banner runs full
        // bleed, so clear the home indicator here instead
        { paddingBottom: Math.max(bottom, SCREEN_PADDING) },
      ]}
      keyboardShouldPersistTaps="handled"
      style={styles.scroll}
    >
      {bannerBlock}
      <View style={styles.body}>
        {bannerTextBlock}
        {termsSummary ? (
          <ScrollView
            contentContainerStyle={styles.termsContent}
            nestedScrollEnabled
            style={styles.termsScroll}
            testID="loyalty-terms-modal.terms"
          >
            <FormattedMarkdown
              markdown={termsSummary}
              textStyle={markdownTextStyle}
            />
          </ScrollView>
        ) : null}
        {termsAcceptanceDisclaimerBlock || termsLinkBlock}
        {emailInputBlock}
        <Button
          disabled={!isEmailValid}
          loading={isSubmitting}
          onPress={() => onAccept(userEmail ? undefined : email)}
          testID="loyalty-terms-modal.accept"
          trackingData={{ name: 'loyalty_terms_modal.accept' }}
          type="primary"
        >
          {i18n.t('loyalty.accept')}
        </Button>
        <Button
          disabled={isSubmitting}
          onPress={onDecline}
          testID="loyalty-terms-modal.decline"
          trackingData={{ name: 'loyalty_terms_modal.decline' }}
          type="tertiary"
        >
          {i18n.t('loyalty.decline')}
        </Button>
      </View>
    </ScrollView>
  );
};

const useStyles = createStyles((theme) => ({
  banner: {
    width: '100%',
  },
  body: {
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: DEFAULT_PADDING,
  },
  emailInput: {
    borderColor: theme.colors.inactiveFill,
    marginBottom: theme.sk.spacing.spaceMedium,
  },
  markdown: {
    ...(theme.sk.typography.paragraph.md as TextStyle),
    color: theme.sk.color.text.secondary,
  },
  markdownSmall: {
    ...(theme.sk.typography.paragraph.sm as TextStyle),
    color: theme.sk.color.text.secondary,
  },
  scroll: {
    flexShrink: 1,
  },
  scrollContent: {
    gap: theme.sk.spacing.spaceSmall,
  },
  termsContent: {
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  termsLink: {
    color: theme.sk.color.text.blue,
    marginTop: theme.sk.spacing.spaceSmall,
    textDecorationLine: 'underline',
  },
  termsScroll: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    maxHeight: 220,
  },
}));
